/**
 * General File Service Utilities
 *
 * Helper functions for general file operations
 */

import type { FileType } from './types';
import { FILE_TYPE_METADATA } from './types';

// Formatting Functions

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format file type for display
 */
export function formatFileType(fileType: FileType): string {
  return FILE_TYPE_METADATA[fileType]?.label || fileType;
}

// Validation Functions

/**
 * Validate file before upload
 */
export function validateFile(
  file: File,
  fileType: FileType
): { valid: boolean; error?: string } {
  const metadata = FILE_TYPE_METADATA[fileType];

  if (!metadata) {
    return { valid: false, error: 'Invalid file type' };
  }

  // Check file size
  if (file.size > metadata.maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds maximum of ${formatFileSize(metadata.maxSizeBytes)}`
    };
  }

  // Check MIME type
  if (!metadata.acceptedMimeTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type ${file.type} is not accepted. Accepted types: ${metadata.acceptedMimeTypes.join(', ')}`
    };
  }

  return { valid: true };
}

// Upload Helpers

/**
 * Check if file is an image
 */
export function isImageFile(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Check if file is a PDF
 */
export function isPdfFile(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

// Cache Key Functions

/**
 * Get cache key for file
 */
export function getFileCacheKey(fileId: number): string[] {
  return ['file', fileId.toString()];
}
